import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * Action Menu Dropdown Component
 * Displays a "..." (kebab) menu with dropdown actions.
 * Uses a Portal + position: fixed so the menu can escape table/scroll containers
 * without ever scrolling the page on open.
 *
 * @param {Array} actions - Array of action objects: { label, icon, onClick, variant }
 * @param {boolean} disabled - Disable the menu button
 */
export default function ActionMenu({ actions = [], disabled = false }) {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const [isOpen, setIsOpen] = useState(false);
  // visibility=hidden until measured to avoid first-paint flash at (0,0)
  const [position, setPosition] = useState({ top: 0, left: 0, ready: false });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Compute (or recompute) the menu position using the real menu size when available.
  const computePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth || 200;
    const menuHeight = menuEl?.offsetHeight || 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    // Vertical: prefer below, flip above only if not enough room.
    const spaceBelow = vh - rect.bottom;
    let top = rect.bottom + 4;
    if (spaceBelow < menuHeight + margin && rect.top > menuHeight + margin) {
      top = rect.top - menuHeight - 4;
    }

    // Horizontal: RTL anchors menu's left edge to button's left edge,
    // LTR anchors menu's right edge to button's right edge.
    let left = isRTL ? rect.left : rect.right - menuWidth;

    // Clamp to viewport.
    left = Math.max(margin, Math.min(left, vw - menuWidth - margin));
    top = Math.max(margin, Math.min(top, vh - menuHeight - margin));

    setPosition({ top, left, ready: true });
  }, [isRTL]);

  // Initial positioning when opened
  useLayoutEffect(() => {
    if (!isOpen) return;
    // First pass with default size estimate
    computePosition();
  }, [isOpen, computePosition]);

  // Second pass after the menu mounts so we can use the real size
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) return;
    computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on outside click, scroll, resize, Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)
      ) {
        close();
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') close();
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    // Use capture for scroll so we catch nested scroll containers too
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen, close]);

  const handleActionClick = (action, event) => {
    // Stop propagation so the click doesn't bubble to a clickable parent row.
    if (event) event.stopPropagation();
    if (action.onClick) action.onClick();
    setIsOpen(false);
  };

  // Wrap toggle so the kebab click doesn't bubble up to a clickable parent
  // (e.g. a table row with onRowClick that would navigate away). Without this,
  // pressing the menu would trigger the row's navigation before the menu opens.
  const toggle = (event) => {
    if (event) event.stopPropagation();
    setPosition(p => ({ ...p, ready: false }));
    setIsOpen(prev => !prev);
  };

  return (
    <>
      {/* Menu Button (Kebab Icon) */}
      <button
        ref={buttonRef}
        onClick={toggle}
        disabled={disabled}
        title={t('common.actions')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          padding: 'var(--spacing-2)',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-base)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--color-text-secondary)',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }
        }}
      >
        <Icon name="more" size="md" color="text-secondary" />
      </button>

      {/* Dropdown Menu — Portal + position: fixed (no page scrolling on open) */}
      {isOpen && createPortal(
        <>
          <style>{`
            @keyframes actionMenuIn {
              from { opacity: 0; transform: translateY(4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.06))',
              zIndex: 10000,
              minWidth: '180px',
              overflow: 'hidden',
              pointerEvents: 'auto',
              visibility: position.ready ? 'visible' : 'hidden',
              animation: position.ready ? 'actionMenuIn 120ms ease-out' : 'none'
            }}
          >
            {actions.map((action, idx) => {
              const isLast = idx === actions.length - 1;
              const isDangerous = action.variant === 'danger';

              return (
                <button
                  key={idx}
                  role="menuitem"
                  onClick={(e) => handleActionClick(action, e)}
                  title={action.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    width: '100%',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: !isLast ? '1px solid var(--color-border)' : 'none',
                    color: isDangerous ? 'var(--color-danger)' : 'var(--color-text-primary)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  onMouseEnter={(e) => {
                    if (isDangerous) {
                      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                      e.currentTarget.style.color = 'var(--color-danger-dark)';
                    } else {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = isDangerous ? 'var(--color-danger)' : 'var(--color-text-primary)';
                  }}
                >
                  {action.icon && (
                    typeof action.icon === 'string' ? (
                      <Icon
                        name={action.icon}
                        size="sm"
                        color={isDangerous ? 'danger' : 'text-primary'}
                        style={{ flexShrink: 0 }}
                      />
                    ) : (
                      <action.icon
                        size={16}
                        style={{
                          flexShrink: 0,
                          color: isDangerous ? 'var(--color-danger)' : 'var(--color-text-primary)'
                        }}
                      />
                    )
                  )}
                  <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{action.label}</span>
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
